import * as React from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// Lightweight Dialog implementation without Radix to avoid runtime hook errors
// Supports controlled and uncontrolled usage, and preserves the same exports/API shape.

type OpenChange = (open: boolean) => void

interface DialogContextValue {
  open: boolean
  setOpen: (open: boolean) => void
}

const DialogContext = React.createContext<DialogContextValue | null>(null)

interface DialogRootProps {
  open?: boolean
  onOpenChange?: OpenChange
  children: React.ReactNode
  /**
   * Accepted for Radix API compatibility. This implementation is always modal
   * (overlay + scroll lock), so the value is ignored.
   */
  modal?: boolean
}

const Dialog = ({ open: controlledOpen, onOpenChange, children }: DialogRootProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? !!controlledOpen : uncontrolledOpen

  const setOpen = React.useCallback((next: boolean) => {
    onOpenChange?.(next)
    if (!isControlled) setUncontrolledOpen(next)
  }, [isControlled, onOpenChange])

  const ctx = React.useMemo(() => ({ open, setOpen }), [open, setOpen])

  return (
    <DialogContext.Provider value={ctx}>{children}</DialogContext.Provider>
  )
}

interface TriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  children: React.ReactElement
}

const DialogTrigger = ({ asChild, children, ...props }: TriggerProps) => {
  const ctx = React.useContext(DialogContext)
  if (!ctx) return children
  const handleClick = (e: React.MouseEvent) => {
    props.onClick?.(e as any)
    ctx.setOpen(true)
  }
  if (asChild) {
    return React.cloneElement(children, {
      ...children.props,
      onClick: handleClick,
    })
  }
  return (
    <button type="button" onClick={handleClick} {...props}>
      {children}
    </button>
  )
}

const DialogPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === 'undefined') return null
  return createPortal(children as any, document.body)
}

interface DialogCloseProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ asChild, children, ...props }, ref) => {
    const ctx = React.useContext(DialogContext)
    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      props.onClick?.(e)
      ctx?.setOpen(false)
    }
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement, {
        onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
          (children as React.ReactElement).props.onClick?.(e)
          onClick(e)
        },
      })
    }
    return (
      <button type="button" ref={ref} onClick={onClick} {...props}>
        {children}
      </button>
    )
  }
)
DialogClose.displayName = 'DialogClose'

const DialogOverlay = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const ctx = React.useContext(DialogContext)
    if (!ctx?.open) return null
    return (
      <div
        ref={ref}
        data-state={ctx.open ? 'open' : 'closed'}
        className={cn(
          "fixed inset-0 z-[9998] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 pointer-events-auto",
          className
        )}
        onClick={() => ctx.setOpen(false)}
        style={{ pointerEvents: 'auto' }}
        {...props}
      />
    )
  }
)
DialogOverlay.displayName = 'DialogOverlay'

const DialogContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & { hideClose?: boolean }>(
  ({ className, children, hideClose, ...props }, ref) => {
    const ctx = React.useContext(DialogContext)

    // Lock body scroll when dialog is open (but allow dialog content to scroll)
    React.useEffect(() => {
      if (!ctx?.open) return

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

      // Lock body scroll
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${scrollbarWidth}px`

      // Prevent touch scrolling on mobile (but allow it inside dialog)
      // Allow interactions inside the dialog content AND interactive portals (e.g., Popovers)
      const preventScroll = (e: TouchEvent) => {
        const target = e.target as HTMLElement
        // Allow if inside dialog content or explicitly allowed interactive portal content
        if (target.closest('[role="dialog"]') || target.closest('[data-dialog-allow-interact]')) {
          return
        }
        e.preventDefault()
      }

      document.addEventListener('touchmove', preventScroll, { passive: false })

      return () => {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
        document.removeEventListener('touchmove', preventScroll)
      }
    }, [ctx?.open])

    // Handle Escape key
    React.useEffect(() => {
      if (!ctx?.open) return

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') ctx.setOpen(false)
      }
      document.addEventListener('keydown', onKeyDown)
      return () => document.removeEventListener('keydown', onKeyDown)
    }, [ctx])

    if (!ctx?.open) return null

    const content = (
      <div data-state={ctx.open ? 'open' : 'closed'}>
        <DialogOverlay />
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          // pointer-events-auto is load-bearing: a Radix popup (DropdownMenu/Select/
          // Popover) opened inside the dialog runs in modal mode and sets
          // `body { pointer-events: none }`. Without an explicit auto here, the
          // content becomes pointer-transparent and the synthesized post-touch click
          // falls through to the overlay beneath, silently closing the dialog on mobile.
          className={cn(
            "pointer-events-auto fixed left-[50%] top-[calc(50%_+_var(--dialog-center-offset,0px))] z-[9999] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl",
            // A centered (top-1/2 / -translate-y-1/2) box with no max-height grows past
            // BOTH viewport edges, and the effect hook below locks body scroll and
            // preventDefaults touchmove outside the dialog — so a tall dialog's header
            // (and its top half generally) was permanently unreachable. Cap the height and
            // let the dialog itself scroll. `overscroll-contain` stops the scroll from
            // chaining out to the locked body. Bottom padding clears the iOS home
            // indicator so footer buttons are tappable; on desktop the inset is 0px and
            // max() resolves to 1.5rem, i.e. identical to the p-6 it overrides.
            // These are ceilings, not overrides: `className` is merged last, so a call
            // site that sets its own max-h / padding still wins (tailwind-merge).
            "max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain pb-[max(1.5rem,env(safe-area-inset-bottom))]",
            className
          )}
          {...props}
        >
          {children}
          {!hideClose && (
            <button
              className={cn(
                "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
                // 16px icon with zero padding = a 16px touch target, far under the 44pt
                // minimum. Grow the HIT AREA only, via a transparent pseudo-element, so the
                // painted X does not move or change size (16 + 2*14 = 44). Mobile-only so
                // desktop mouse behaviour is untouched.
                "max-[937px]:after:absolute max-[937px]:after:inset-x-[-14px] max-[937px]:after:inset-y-[-14px] max-[937px]:after:content-['']"
              )}
              onClick={() => ctx.setOpen(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    )

    return <DialogPortal>{content}</DialogPortal>
  }
)
DialogContent.displayName = 'DialogContent'

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} className={cn("text-lg font-semibold leading-none tracking-tight", className)} {...props} />
  )
)
DialogTitle.displayName = 'DialogTitle'

const DialogDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
)
DialogDescription.displayName = 'DialogDescription'

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
